
import React from 'react';
import { Calendar, Layers, Coins, Clock, Globe, Tag, ExternalLink } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PremiumBot } from '@/types/premium-bots';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';
import { formatNumber } from '@/components/premium-bots/utils/formatting';
import { ExchangeBadges } from '@/components/dashboard/bot-card/ExchangeBadges';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';

interface EnhancedBotInfoSectionProps {
  bot: PremiumBot | null;
  isLoading: boolean;
}

const InfoRow = ({ 
  icon: Icon, 
  label, 
  value, 
  color = 'default',
  isMobile = false 
}: {
  icon: any;
  label: string;
  value: React.ReactNode;
  color?: 'default' | 'primary' | 'success' | 'warning';
  isMobile?: boolean;
}) => {
  const getColorClasses = (color: string) => {
    const colors = {
      default: {
        bg: 'bg-muted/50',
        text: 'text-muted-foreground',
        hover: 'group-hover:bg-muted/80'
      },
      primary: {
        bg: 'bg-primary/10',
        text: 'text-primary',
        hover: 'group-hover:bg-primary/20'
      },
      success: {
        bg: 'bg-green-500/10',
        text: 'text-green-500',
        hover: 'group-hover:bg-green-500/20'
      },
      warning: {
        bg: 'bg-amber-500/10',
        text: 'text-amber-500',
        hover: 'group-hover:bg-amber-500/20'
      }
    };
    return colors[color as keyof typeof colors] || colors.default;
  };

  const colorClasses = getColorClasses(color);

  return (
    <div className="group relative overflow-hidden rounded-lg border border-border/50 bg-gradient-to-r from-background via-muted/5 to-background p-4 transition-all duration-300 hover:shadow-md hover:border-border hover:-translate-y-0.5">
      {/* Background gradient on hover */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* Enhanced Icon */}
          <div className={`relative ${isMobile ? "w-8 h-8" : "w-10 h-10"} ${colorClasses.bg} ${colorClasses.hover} rounded-lg flex items-center justify-center transition-all duration-300 group-hover:scale-110`}>
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <Icon className={`${isMobile ? "h-4 w-4" : "h-5 w-5"} ${colorClasses.text} transition-transform duration-300 group-hover:rotate-6 relative z-10`} />
          </div>
          
          {/* Label */}
          <span className={`${isMobile ? "text-sm" : "text-base"} text-muted-foreground transition-colors duration-300 group-hover:text-foreground`}>
            {label}
          </span>
        </div>
        
        {/* Value */}
        <div className="flex items-center gap-2">
          {value}
          
          {/* Animated arrow on hover */}
          <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-2 group-hover:translate-x-0">
            <ExternalLink className="h-3 w-3 text-muted-foreground" />
          </div>
        </div>
      </div>
      
      {/* Progress line */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-primary/50 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-center" />
    </div>
  );
};

export const EnhancedBotInfoSection = ({
  bot,
  isLoading
}: EnhancedBotInfoSectionProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  
  if (isLoading) {
    return (
      <Card className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-background to-purple-500/5" />
        <CardHeader className={`pb-4 relative ${isMobile ? "p-4" : "px-6 py-4"}`}>
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent className={`relative ${isMobile ? "px-4 pb-4" : "px-6 pb-6"}`}>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5, 6, 7].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return t('not_available');
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch (e) {
      return dateString;
    }
  };

  const getBotTypeColor = (type?: string) => {
    return type === 'premium_bot' ? 'primary' : 'default';
  };

  const getCapitalColor = (capital?: number) => {
    if (!capital) return 'default';
    if (capital >= 10000) return 'warning';
    if (capital >= 1000) return 'success';
    return 'default';
  };

  return (
    <Card className="relative overflow-hidden">
      {/* Enhanced Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-background to-purple-500/5" />
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-blue-500/10 to-transparent rounded-full blur-2xl" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-purple-500/10 to-transparent rounded-full blur-xl" />
      
      <CardHeader className={`pb-4 relative ${isMobile ? "p-4" : "px-6 py-4"}`}>
        <CardTitle className={`${isMobile ? "text-lg" : "text-xl"} flex items-center gap-3`}>
          <div className="w-1 h-6 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full" />
          {t('bot_information')}
          <div className="ml-auto">
            <Badge variant="outline" className="bg-blue-500/20 text-blue-500 border-blue-500/30">
              <Tag className={`${isMobile ? "mr-1 h-3 w-3" : "mr-1 h-4 w-4"}`} />
              {t('detailed_info')}
            </Badge>
          </div>
        </CardTitle>
      </CardHeader>
      
      <CardContent className={`relative ${isMobile ? "px-4 pb-4" : "px-6 pb-6"}`}>
        <div className="space-y-4">
          {/* Bot Type */}
          <InfoRow
            icon={Tag}
            label={t('bot_type')}
            value={
              <Badge 
                variant="outline" 
                className={getBotTypeColor(bot?.type) === 'primary' ? 
                  "bg-primary/20 text-primary border-primary/30" : 
                  "bg-muted text-muted-foreground"
                }
              >
                {bot?.type === 'premium_bot' ? t('premium_bot') : bot?.type || t('not_available')}
              </Badge>
            }
            color={getBotTypeColor(bot?.type)}
            isMobile={isMobile}
          />

          {/* Platform */}
          <InfoRow
            icon={Layers}
            label={t('platform')}
            value={<ExchangeBadges exchanges={bot?.exchange || []} />}
            color="success"
            isMobile={isMobile}
          />

          {/* Min Capital */}
          <InfoRow
            icon={Coins}
            label={t('min_capital')}
            value={
              <div className="flex items-center gap-2">
                <span className={`font-semibold ${isMobile ? "text-sm" : "text-base"}`}>
                  ${formatNumber(bot?.premiumDetails?.min_capital || 0)}
                </span>
                {(bot?.premiumDetails?.min_capital || 0) >= 10000 && (
                  <Badge variant="outline" className="bg-amber-500/20 text-amber-500 border-amber-500/30 text-xs">
                    {t('high_capital')}
                  </Badge>
                )}
              </div>
            }
            color={getCapitalColor(bot?.premiumDetails?.min_capital)}
            isMobile={isMobile}
          />

          {/* Trading Style */}
          <InfoRow
            icon={Tag}
            label={t('trading_style')}
            value={
              <Badge variant="secondary" className="bg-gradient-to-r from-muted to-muted/80">
                {bot?.premiumDetails?.trading_style || t('not_available')}
              </Badge>
            }
            isMobile={isMobile}
          />

          {/* Timeframe */}
          <InfoRow
            icon={Clock}
            label={t('timeframe')}
            value={
              <span className={`font-medium ${isMobile ? "text-sm" : "text-base"}`}>
                {bot?.premiumDetails?.timeframe || t('not_available')}
              </span>
            }
            color="primary"
            isMobile={isMobile}
          />

          {/* Markets */}
          <InfoRow
            icon={Globe}
            label={t('markets')}
            value={
              <div className="flex flex-wrap gap-1">
                {bot?.premiumDetails?.markets?.map((market, index) => (
                  <Badge 
                    key={index}
                    variant="outline" 
                    className={`${isMobile ? "text-xs" : "text-sm"} bg-green-500/20 text-green-500 border-green-500/30`}
                  >
                    {market}
                  </Badge>
                )) || (
                  <span className="text-muted-foreground">{t('not_available')}</span>
                )}
              </div>
            }
            color="success"
            isMobile={isMobile}
          />

          {/* Created Date */}
          <InfoRow
            icon={Calendar}
            label={t('created_date')}
            value={
              <span className={`font-medium ${isMobile ? "text-sm" : "text-base"}`}>
                {formatDate(bot?.premiumDetails?.created_date || bot?.created_date)}
              </span>
            }
            isMobile={isMobile}
          />
          
          {/* Profit Share */}
          <InfoRow
            icon={Coins}
            label={t('profit_share')}
            value={
              <div className="flex items-center gap-2">
                <span className={`font-bold ${isMobile ? "text-sm" : "text-base"} text-green-500`}>
                  {bot?.premiumDetails?.profit_share}%
                </span>
                <Badge variant="outline" className="bg-green-500/20 text-green-500 border-green-500/30 text-xs">
                  {t('revenue_share')}
                </Badge>
              </div>
            }
            color="success"
            isMobile={isMobile}
          />
        </div>
        
        {/* Enhanced Footer */}
        <div className="mt-6 pt-4 border-t border-border/30">
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full animate-pulse" />
            <span>{t('all_information_verified')}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
